import { KeyboardArrowDown } from "@mui/icons-material";
import { createTheme } from "@mui/material";

export const themeV2 = createTheme({
  shape: {
    borderRadius: 6,
  },
  typography: {
    fontFamily: "Montserrat, sans-serif",
    fontWeightLight: 400,
    fontWeightRegular: 500,
    fontWeightMedium: 600,
    fontWeightBold: 700,
    body1: {
      fontWeight: 400,
      fontSize: 14,
      lineHeight: "150%",
    },
    body2: {
      fontWeight: 400,
      fontSize: 12,
      lineHeight: "150%",
    },
    subtitle1: {
      fontWeight: 400,
      fontSize: 16,
      lineHeight: "150%",
    },
    subtitle2: {
      fontWeight: 400,
      fontSize: 18,
      lineHeight: "150%",
    },
    h2: {
      fontWeight: 700,
      fontSize: 30,
      lineHeight: "150%",
    },
    h6: {
      fontWeight: 500,
      fontSize: 20,
      lineHeight: "150%",
    },
  },
  palette: {
    primary: {
      main: "#1d4ed8", // Your specified primary blue
      light: "#eff6ff", // Your specified light primary
      dark: "#1e3a8a", // For titles and emphasis
    },
    secondary: {
      main: "#16a34a", // Your specified secondary green
      light: "#f0fdf4",
      dark: "#15803d",
    },
    text: {
      primary: "#0f172a", // Better shade of black for text
      secondary: "#475569", // Medium gray for secondary text
    },
    divider: "#e2e8f0", // Light gray divider
    background: {
      default: "#ffffff", // Clean white default
      paper: "#ffffff",
    },
    // Custom colors for the new theme
    info: {
      main: "#0ea5e9", // Light blue accent
      light: "#f0f9ff",
    },
    success: {
      main: "#16a34a", // Matches secondary
      light: "#f0fdf4",
    },
    warning: {
      main: "#f59e0b",
      light: "#fef3c7",
    },
    error: {
      main: "#dc2626",
      light: "#fef2f2",
    },
  },
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          boxSizing: "border-box",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#e2e8f0",
            borderRadius: "12px",
            borderWidth: "2px",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#1d4ed8",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#1d4ed8",
          },
          "& .MuiSvgIcon-root": {
            color: "#64748b",
            fontSize: "20px",
          },
        },
        input: {
          height: "48px",
          boxSizing: "border-box",
          fontWeight: 500,
          fontSize: "14px",
          color: "#0f172a",
          "&::placeholder": {
            fontWeight: 400,
            fontSize: "14px",
            color: "#94a3b8",
            opacity: 1,
          },
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        outlined: {
          padding: "12px 16px",
          height: "46px",
          display: "flex",
          alignItems: "center",
          boxSizing: "border-box",
          color: "#0f172a",
        },
      },
      defaultProps: {
        IconComponent: KeyboardArrowDown,
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "capitalize",
          fontSize: "13px",
          height: "36px",
          padding: "12px 16px",
          fontWeight: 600,
        },
        contained: {
          backgroundColor: "#1d4ed8",
          boxShadow: "0 4px 12px rgba(29, 78, 216, 0.3)",
          "&:hover": {
            backgroundColor: "#1e40af",
            boxShadow: "0 6px 16px rgba(29, 78, 216, 0.4)",
          },
        },
        containedSuccess: {
          backgroundColor: "#16a34a",
          boxShadow: "0 4px 12px rgba(22, 163, 74, 0.3)",
          "&:hover": {
            backgroundColor: "#15803d",
            boxShadow: "0 6px 16px rgba(22, 163, 74, 0.4)",
          },
        },
        outlinedError: {
          borderColor: "#dc2626",
          color: "#dc2626",
          "&:hover": {
            borderColor: "#b91c1c",
            backgroundColor: "#fef2f2",
          },
        },
        outlined: {
          borderColor: "#1d4ed8",
          color: "#1d4ed8",
          "&:hover": {
            borderColor: "#1e40af",
            backgroundColor: "#eff6ff",
          },
        },
      },
    },

    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          fontSize: "14px",
          fontWeight: 500,
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
        },
        root: {
          "& .MuiInputBase-root": {
            height: "46px",
            "& input": {
              height: "100%",
              color: "#0f172a",
            },
          },
        },
      },
    },

    MuiSvgIcon: {
      defaultProps: {
        fontSize: "small",
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          marginTop: "8px",
          borderRadius: "12px",
          boxShadow: "0 1px 4px rgba(0, 0, 0, 0.05)",
          border: "1px solid #e2e8f0",
          background: "#ffffff",
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderBottom: "1px solid #f1f5f9",
          "&:last-of-type": {
            borderBottom: "none",
          },
          "&:hover": {
            backgroundColor: "#eff6ff",
          },
          fontSize: "13px",
          fontWeight: 500,
        },
      },
    },

    MuiRadio: {
      styleOverrides: {
        root: {
          color: "#cbd5e1",
          "&:hover": {
            backgroundColor: "rgba(29, 78, 216, 0.04)",
          },
          "&.Mui-checked": {
            color: "#1d4ed8",
            "&:hover": {
              backgroundColor: "rgba(29, 78, 216, 0.04)",
            },
          },
          "& .MuiSvgIcon-root": {
            fontSize: 20,
          },
        },
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: "#cbd5e1",
          borderRadius: "6px",
          "&:hover": {
            backgroundColor: "rgba(29, 78, 216, 0.04)",
          },
          "&.Mui-checked": {
            color: "#1d4ed8",
            "&:hover": {
              backgroundColor: "rgba(29, 78, 216, 0.04)",
            },
          },
          "& .MuiSvgIcon-root": {
            fontSize: 20,
          },
        },
      },
    },

    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          margin: "8px 0",
          "&:hover": {
            "& .MuiRadio-root, & .MuiCheckbox-root": {
              backgroundColor: "rgba(29, 78, 216, 0.04)",
            },
          },
        },
        label: {
          fontSize: "14px",
          fontWeight: 500,
          color: "#0f172a",
          marginLeft: "8px",
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
          borderRadius: "16px",
          border: "1px solid #e2e8f0",
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
          borderRadius: "16px",
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
        },
        filled: {
          backgroundColor: "#1d4ed8",
          color: "white",
        },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: "4px",
          backgroundColor: "#f1f5f9",
        },
        bar: {
          borderRadius: "4px",
          background: "linear-gradient(135deg, #1d4ed8 0%, #16a34a 100%)",
        },
      },
    },
  },
});
